// src/pages/ManageDoctors.jsx - COMPLETE WITH SANITIZATION
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  listDoctors,
  addDoctor,
  updateDoctor,
  deleteDoctor,
} from "../../Redux/actions/adminActions/doctorActions.js";
import { listDepartments } from "../../Redux/actions/adminActions/departmentActions.js";
import Loading from "../../components/Loading.jsx";
import Error from "../../components/Error.jsx";
import { showToast } from "../../components/ToastMessage.jsx";
import { useSanitizedInput } from "../../hooks/useSanitizedInput.js"; // ✅ Add import
import { 
  UserPlus, 
  Search, 
  Edit2, 
  Trash2, 
  X, 
  Save, 
  Upload, 
  Stethoscope,
  Mail,
  Phone,
  Award,
  Building2,
  FileText,
  Lock,
  User
} from "lucide-react";

const ManageDoctors = () => {
  const dispatch = useDispatch();

  const { loading, error, doctors } = useSelector(
    (state) => state.adminDoctors || {}
  );
  const { departments } = useSelector((state) => state.departments || {});

  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentDoctor, setCurrentDoctor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ Replace useState with sanitized hooks for text inputs
  const [name, handleNameChange, resetName, setName] = useSanitizedInput("", "alphanumeric");
  const [email, handleEmailChange, resetEmail, setEmail] = useSanitizedInput("", "email");
  const [password, handlePasswordChange, resetPassword, setPassword] = useSanitizedInput("", "text");
  const [specialization, handleSpecializationChange, resetSpecialization, setSpecialization] = useSanitizedInput("", "alphanumeric");
  const [contact, handleContactChange, resetContact, setContact] = useSanitizedInput("", "phone");
  const [description, handleDescriptionChange, resetDescription, setDescription] = useSanitizedInput("", "text");
  
  // ✅ Keep these as regular state (numeric and dropdown)
  const [experience, setExperience] = useState("");
  const [department, setDepartment] = useState("");

  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    dispatch(listDoctors());
    dispatch(listDepartments());
  }, [dispatch]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const openAddForm = () => {
    setShowForm(true);
    setEditMode(false);
    
    // ✅ Reset all fields using hook resetters
    resetName();
    resetEmail();
    resetPassword();
    resetSpecialization();
    resetContact();
    resetDescription();
    setExperience("");
    setDepartment("");
    setImage(null);
    setImagePreview(null);
  };

  // ✅ Update openEditForm to use setters
  const openEditForm = (doctor) => {
    setShowForm(true);
    setEditMode(true);
    setCurrentDoctor(doctor);
    
    // ✅ Use direct setters for programmatic updates
    setName(doctor.name);
    setEmail(doctor.email);
    setPassword("");
    setSpecialization(doctor.specialization);
    setExperience(doctor.experience);
    setDescription(doctor.description);
    setContact(doctor.contact);
    setDepartment(doctor.department || "");

    if (doctor.image) {
      const imagePath = doctor.image.startsWith("http")
        ? doctor.image
        : `https://shoaibhospital.cloud${doctor.image}`;
      setImagePreview(imagePath);
    } else {
      setImagePreview("/default-doctor.png");
    }
  };

  // ✅ Add validation before submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ✅ Validate length limits
    if (name.length > 100) {
      showToast("Name is too long (max 100 characters)", "error");
      return;
    }
    
    if (specialization.length > 100) {
      showToast("Specialization is too long (max 100 characters)", "error");
      return;
    }
    
    if (description.length > 1000) {
      showToast("Description is too long (max 1000 characters)", "error");
      return;
    }
    
    if (contact.length > 20) {
      showToast("Contact number is too long (max 20 characters)", "error");
      return;
    }
    
    if (!editMode && password.length < 6) {
      showToast("Password must be at least 6 characters", "error");
      return;
    }
    
    if (editMode && password && password.length < 6) {
      showToast("Password must be at least 6 characters", "error");
      return;
    }
    
    if (password.length > 100) {
      showToast("Password is too long (max 100 characters)", "error");
      return;
    }
    
    if (experience < 0 || experience > 100) {
      showToast("Experience must be between 0 and 100 years", "error");
      return;
    }

    try {
      const data = new FormData();
      
      // ✅ Use sanitized values
      data.append("name", name);
      data.append("email", email);
      if (password) data.append("password", password);
      data.append("specialization", specialization);
      data.append("experience", experience);
      data.append("description", description);
      data.append("contact", contact);
      data.append("department", department);
      
      if (image) data.append("image", image);

      if (editMode) {
        await dispatch(updateDoctor(currentDoctor._id, data));
        showToast("Doctor updated successfully");
      } else {
        await dispatch(addDoctor(data));
        showToast("Doctor added successfully");
      }

      setShowForm(false);
      dispatch(listDoctors());
    } catch (error) {
      showToast(error.response?.data?.msg || "Error saving doctor", "error");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this doctor?")) {
      try {
        await dispatch(deleteDoctor(id));
        showToast("Doctor deleted successfully");
        dispatch(listDoctors());
      } catch (error) {
        showToast(error.response?.data?.msg || "Error deleting doctor", "error");
      }
    }
  };

  const filteredDoctors = doctors?.filter((doc) => {
    const search = searchTerm.toLowerCase();
    return (
      doc.name?.toLowerCase().includes(search) ||
      doc.specialization?.toLowerCase().includes(search) ||
      doc.contact?.toLowerCase().includes(search) ||
      (doc.department?.toLowerCase() || "").includes(search)
    );
  });

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "2rem" }}>
      <div className="container-fluid">
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                padding: "2rem",
                borderRadius: "16px",
                color: "white",
                boxShadow: "0 8px 24px rgba(102, 126, 234, 0.25)",
              }}
            >
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                  <h2 className="mb-2 fw-bold d-flex align-items-center">
                    <Stethoscope size={32} className="me-3" />
                    Doctor Management
                  </h2>
                  <p className="mb-0" style={{ opacity: 0.9 }}>
                    Manage hospital doctors and their information
                  </p>
                </div>
                {!showForm && (
                  <button
                    className="btn btn-light btn-lg d-flex align-items-center"
                    onClick={openAddForm}
                    style={{
                      borderRadius: "12px",
                      padding: "0.75rem 2rem",
                      fontWeight: "600",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    }}
                  >
                    <UserPlus size={20} className="me-2" />
                    Add Doctor
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        {!showForm && (
          <div className="row mb-4">
            <div className="col-md-4">
              <div
                className="card"
                style={{
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="mb-1" style={{ opacity: 0.9, fontSize: "0.9rem" }}>
                        Total Doctors
                      </p>
                      <h2 className="mb-0 fw-bold">{doctors?.length || 0}</h2>
                    </div>
                    <div
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: "12px",
                        padding: "1rem",
                      }}
                    >
                      <Stethoscope size={32} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div
                className="card"
                style={{
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  color: "white",
                }}
              >
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="mb-1" style={{ opacity: 0.9, fontSize: "0.9rem" }}>
                        Search Results
                      </p>
                      <h2 className="mb-0 fw-bold">{filteredDoctors?.length || 0}</h2>
                    </div>
                    <div
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: "12px",
                        padding: "1rem",
                      }}
                    >
                      <Search size={32} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div
                className="card"
                style={{
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                  color: "white",
                }}
              >
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="mb-1" style={{ opacity: 0.9, fontSize: "0.9rem" }}>
                        Departments
                      </p>
                      <h2 className="mb-0 fw-bold">{departments?.length || 0}</h2>
                    </div>
                    <div
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: "12px",
                        padding: "1rem",
                      }}
                    >
                      <Building2 size={32} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Bar */}
        {!showForm && (
          <div className="row mb-4">
            <div className="col-12">
              <div
                className="card"
                style={{
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                <div className="card-body p-4">
                  <div className="position-relative">
                    <Search
                      size={20}
                      style={{
                        position: "absolute",
                        left: "1rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#6c757d",
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Search by name, specialization, contact or department..."
                      className="form-control form-control-lg"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        paddingLeft: "3rem",
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                        fontSize: "1rem",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-5">
            <Loading />
          </div>
        )}
        {error && (
          <div className="row">
            <div className="col-12">
              <Error message={error} />
            </div>
          </div>
        )}

        {/* Table View */}
        {!showForm && !loading && !error && (
          <div className="row">
            <div className="col-12">
              <div
                className="card"
                style={{
                  borderRadius: "16px",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                <div className="card-body p-0">
                  <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto" }}>
                    <table className="table table-hover mb-0" style={{ minWidth: "1200px" }}>
                      <thead
                        style={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th
                            style={{
                              padding: "1.25rem 1.5rem",
                              fontWeight: 600,
                              fontSize: "0.95rem",
                              borderBottom: "none",
                            }}
                          >
                            Doctor
                          </th>
                          <th
                            style={{
                              padding: "1.25rem 1.5rem",
                              fontWeight: 600,
                              fontSize: "0.95rem",
                              borderBottom: "none",
                            }}
                          >
                            Specialization
                          </th>
                          <th
                            style={{
                              padding: "1.25rem 1.5rem",
                              fontWeight: 600,
                              fontSize: "0.95rem",
                              borderBottom: "none",
                            }}
                          >
                            Experience
                          </th>
                          <th
                            style={{
                              padding: "1.25rem 1.5rem",
                              fontWeight: 600,
                              fontSize: "0.95rem",
                              borderBottom: "none",
                            }}
                          >
                            Contact
                          </th>
                          <th
                            style={{
                              padding: "1.25rem 1.5rem",
                              fontWeight: 600,
                              fontSize: "0.95rem",
                              borderBottom: "none",
                            }}
                          >
                            Department
                          </th>
                          <th
                            style={{
                              padding: "1.25rem 1.5rem",
                              fontWeight: 600,
                              fontSize: "0.95rem",
                              textAlign: "center",
                              borderBottom: "none",
                            }}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDoctors && filteredDoctors.length > 0 ? (
                          filteredDoctors.map((doc) => (
                            <tr
                              key={doc._id}
                              style={{
                                borderBottom: "1px solid #f0f0f0",
                                transition: "background 0.2s",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f8f9fa")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "transparent")
                              }
                            >
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <div className="d-flex align-items-center gap-3">
                                  <img
                                    src={
                                      doc.image
                                        ? doc.image.startsWith("http")
                                          ? doc.image
                                          : `https://shoaibhospital.cloud${doc.image}`
                                        : "/default-doctor.png"
                                    }
                                    alt="doctor"
                                    style={{
                                      width: "50px",
                                      height: "50px",
                                      borderRadius: "12px",
                                      objectFit: "cover",
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    }}
                                  />
                                  <div>
                                    <div className="fw-semibold" style={{ fontSize: "1rem" }}>
                                      Dr. {doc.name}
                                    </div>
                                    <small className="text-muted">{doc.email}</small>
                                  </div>
                                </div>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    style={{
                                      background: "#e3f2fd",
                                      borderRadius: "8px",
                                      padding: "0.4rem",
                                    }}
                                  >
                                    <Award size={16} color="#667eea" />
                                  </div>
                                  <span>{doc.specialization}</span>
                                </div>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <span
                                  className="badge"
                                  style={{
                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    color: "white",
                                    padding: "0.5rem 1rem",
                                    fontSize: "0.85rem",
                                    fontWeight: "500",
                                  }}
                                >
                                  {doc.experience} years
                                </span>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <div className="d-flex align-items-center gap-2">
                                  <Phone size={16} color="#6c757d" />
                                  <span>{doc.contact}</span>
                                </div>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <div className="d-flex align-items-center gap-2">
                                  <Building2 size={16} color="#6c757d" />
                                  <span>{doc.department || "-"}</span>
                                </div>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem", textAlign: "center" }}>
                                <div className="d-flex gap-2 justify-content-center">
                                  <button
                                    className="btn btn-sm btn-outline-primary d-flex align-items-center"
                                    onClick={() => openEditForm(doc)}
                                    style={{ borderRadius: "8px", padding: "0.4rem 1rem" }}
                                  >
                                    <Edit2 size={14} className="me-1" />
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger d-flex align-items-center"
                                    onClick={() => handleDelete(doc._id)}
                                    style={{ borderRadius: "8px", padding: "0.4rem 1rem" }}
                                  >
                                    <Trash2 size={14} className="me-1" />
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="6"
                              className="text-center py-5"
                              style={{ color: "#6c757d" }}
                            >
                              <Stethoscope size={64} color="#dee2e6" className="mb-3" />
                              <p className="mb-0 fw-semibold" style={{ fontSize: "1.1rem" }}>
                                No doctors found
                              </p>
                              <p className="text-muted small">
                                {searchTerm
                                  ? "Try adjusting your search criteria"
                                  : "Click 'Add Doctor' to create your first doctor profile"}
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form View */}
        {showForm && (
          <div className="row">
            <div className="col-12">
              <div
                className="card"
                style={{
                  borderRadius: "16px",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                <div
                  className="card-header"
                  style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    borderRadius: "16px 16px 0 0",
                    padding: "1.5rem 2rem",
                    border: "none",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h4 className="mb-0 fw-bold">
                      {editMode ? "Edit Doctor" : "Add New Doctor"}
                    </h4>
                    <button
                      className="btn btn-light btn-sm"
                      onClick={() => setShowForm(false)}
                      style={{ borderRadius: "8px" }}
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
                <div className="card-body p-4">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-4">
                      {/* Name */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                          <User size={16} color="#667eea" />
                          Full Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          className="form-control form-control-lg"
                          value={name}
                          onChange={handleNameChange} // ✅ Use sanitized handler
                          placeholder="Dr. John Smith"
                          required
                          maxLength={100} // ✅ Add max length
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </div>

                      {/* Email */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                          <Mail size={16} color="#667eea" />
                          Email Address
                        </label>
                        <input
                          type="email"
                          name="email"
                          className="form-control form-control-lg"
                          value={email}
                          onChange={handleEmailChange} // ✅ Use sanitized handler
                          placeholder="doctor@hospital.com"
                          required
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </div>

                      {/* Password */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                          <Lock size={16} color="#667eea" />
                          Password {editMode && <small className="text-muted">(leave blank to keep current)</small>}
                        </label>
                        <input
                          type="password"
                          name="password"
                          className="form-control form-control-lg"
                          value={password}
                          onChange={handlePasswordChange} // ✅ Use sanitized handler
                          placeholder={editMode ? "Leave blank to keep current" : "Enter password"}
                          required={!editMode}
                          maxLength={100} // ✅ Add max length
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </div>

                      {/* Contact */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                          <Phone size={16} color="#667eea" />
                          Contact Number
                        </label>
                        <input
                          type="text"
                          name="contact"
                          className="form-control form-control-lg"
                          value={contact}
                          onChange={handleContactChange} // ✅ Use sanitized handler
                          placeholder="+1234567890"
                          required
                          maxLength={20} // ✅ Add max length
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </div>

                      {/* Specialization */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                          <Award size={16} color="#667eea" />
                          Specialization
                        </label>
                        <input
                          type="text"
                          name="specialization"
                          className="form-control form-control-lg"
                          value={specialization}
                          onChange={handleSpecializationChange} // ✅ Use sanitized handler
                          placeholder="Cardiology, Neurology, etc."
                          required
                          maxLength={100} // ✅ Add max length
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </div>

                      {/* Experience */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                          <Award size={16} color="#667eea" />
                          Experience (Years)
                        </label>
                        <input
                          type="number"
                          name="experience"
                          className="form-control form-control-lg"
                          value={experience}
                          onChange={(e) => setExperience(e.target.value)}
                          placeholder="5"
                          required
                          min="0" // ✅ Add min
                          max="100" // ✅ Add max
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </div>

                      {/* Department */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                          <Building2 size={16} color="#667eea" />
                          Department
                        </label>
                        <select
                          className="form-select form-select-lg"
                          name="department"
                          value={department}
                          onChange={(e) => setDepartment(e.target.value)}
                          required
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        >
                          <option value="">Select Department</option>
                          {departments &&
                            departments.map((dept) => (
                              <option key={dept._id} value={dept.name}>
                                {dept.name}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* Image Upload */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                          <Upload size={16} color="#667eea" />
                          Doctor Image
                        </label>
                        <input
                          type="file"
                          className="form-control form-control-lg"
                          accept="image/*"
                          onChange={handleImageChange}
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                        {imagePreview && (
                          <div className="mt-3">
                            <img
                              src={imagePreview}
                              alt="preview"
                              style={{
                                width: "120px",
                                height: "120px",
                                objectFit: "cover",
                                borderRadius: "12px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Description */}
                      <div className="col-12">
                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                          <FileText size={16} color="#667eea" />
                          Description / Bio (Optional)
                        </label>
                        <textarea
                          name="description"
                          className="form-control"
                          value={description}
                          onChange={handleDescriptionChange} // ✅ Use sanitized handler
                          rows="4"
                          placeholder="Brief description about the doctor's expertise, achievements, etc."
                          maxLength={1000} // ✅ Add max length
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </div>
                    </div>

                    <div className="d-flex justify-content-end gap-3 mt-4">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-lg d-flex align-items-center"
                        onClick={() => setShowForm(false)}
                        style={{ borderRadius: "10px", padding: "0.6rem 2rem" }}
                      >
                        <X size={18} className="me-2" />
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-lg d-flex align-items-center"
                        style={{
                          borderRadius: "10px",
                          padding: "0.6rem 2rem",
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          border: "none",
                          color: "white",
                          fontWeight: "600",
                        }}
                      >
                        <Save size={18} className="me-2" />
                        {editMode ? "Update Doctor" : "Add Doctor"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageDoctors;